'use client';

import { useDownloadFile } from '@newfront-insurance/admin-ui/files';
import { Badge, cn } from '@newfront-insurance/core-ui/v2';
import { DollarSign, FileIcon, Loader2, Shield } from 'lucide-react';

import { useOpenChatWorkspaceFile } from '../../hooks/use-chat-workspace';
import {
  getAttachedFileSourceMetadata,
  getDataExplorerSourceMetadata,
  getSourceMetadata,
  type SourcePart,
} from '../chat-v2/lib/sources';

export type SourceBadgeProps = {
  className?: string;
  source: SourcePart;
  index: number;
  size?: 'sm' | 'default';
};

function EntityTypeIcon({ entityType }: { entityType: string }) {
  switch (entityType.toLowerCase()) {
    case 'claim':
    case 'invoice':
      return <DollarSign className="text-steel-200 h-4 w-3.5" />;
    case 'policy':
    default:
      return <Shield className="text-steel-200 h-4 w-3.5" />;
  }
}

export function SourceBadge({ source, size = 'default', className }: SourceBadgeProps) {
  const { preview, isDownloading } = useDownloadFile({});
  const dataExplorerMeta = getDataExplorerSourceMetadata(source);
  const documentSearchMeta = getSourceMetadata(source);
  const attachedFileMeta = getAttachedFileSourceMetadata(source);
  const fileUuid = documentSearchMeta?.fileUuid ?? attachedFileMeta?.fileUuid ?? undefined;
  const workspacePath = fileUuid ? undefined : (attachedFileMeta?.path ?? undefined);
  const openWorkspaceFile = useOpenChatWorkspaceFile();
  const badge = (
    <Badge
      variant="secondary"
      className={cn(
        'border-steel-200 text-steel-500 hover:bg-steel-50 inline-flex cursor-pointer',
        'items-center justify-center gap-[5px] rounded-[20px] border bg-white py-2 pr-2 pl-1.5',
        'text-center text-[12px] leading-4 font-normal whitespace-nowrap shadow-none transition-colors',
        size === 'sm' && 'px-2 py-0.5',
        isDownloading && 'opacity-80',
      )}
    >
      {isDownloading ? (
        <Loader2 className="text-steel-200 h-4 w-3.5 animate-spin" />
      ) : dataExplorerMeta ? (
        <EntityTypeIcon entityType={dataExplorerMeta.entityType} />
      ) : attachedFileMeta ? (
        <FileIcon className="text-steel-200 h-4 w-3.5" />
      ) : null}
      {source.title}
    </Badge>
  );

  if (fileUuid || workspacePath) {
    return (
      <button
        type="button"
        className={cn('no-underline disabled:cursor-progress', className)}
        disabled={isDownloading}
        aria-busy={isDownloading}
        onClick={() =>
          fileUuid
            ? void preview(fileUuid, source.title)
            : openWorkspaceFile({ path: workspacePath!, page: attachedFileMeta?.pageNumber })
        }
      >
        {badge}
      </button>
    );
  }

  const url = 'url' in source ? source.url : undefined;

  if (!url) {
    return <span className={className}>{badge}</span>;
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={cn('no-underline', className)}>
      {badge}
    </a>
  );
}
